import React from 'react'

import { HeatGridPlot } from '../../plots/heat-grid/HeatGridPlot'
import { CHART_SEGMENT_CONTROL_CLEARANCE_X } from '../chart-segment/ChartSegmentControl'
import { CHART_SEGMENT_GEAR_INSET } from '../chart-segment/gear-layout'
import { readCutSplit } from '../../plots/heat-grid/readAdapters'
import { ReadPill } from './ReadPill'

import type { SegmentRow, SegmentSplit } from '@digital-twins/slide-schemas/generated/slide-types'

const MUTED = 'var(--slide-text-muted, #737373)'
const SECONDARY = 'var(--slide-text-secondary, #384242)'
/** The group label box (`slide-boxes.json` `segmentRowLabel`). */
const SPLIT_ROW_LABEL_PX = 184
const SPLIT_PAD_X = 20

/**
 * A choice question drawn split by one computed cut, in place of its bars
 * (cuts throughout, Step 4). One grid, groups by options, drawn as the shared
 * `heat-grid` plot in its static mode (plots plan Q): the plot Python stamped
 * on the split (`segmentSplit.plot`), else the stored split through its
 * legacy read adapter. Every figure is the builder's, printed at the split's
 * display decimals. Around it: the question heading, the split's title, each
 * group's base and read pill, and the split's read line and notes.
 *
 * Nothing here is computed or translated: deck-ui has no translation runtime.
 * No label is clipped: the builder budgets each against its measured box
 * (`slide-boxes.json`) and a longer one ships whole with a typed warning.
 */
export function SegmentSplitChart(props: {
  slideType: 'single-select-bars' | 'multi-select-bars'
  chartTitle: string
  rows: ReadonlyArray<SegmentRow>
  split: SegmentSplit
}) {
  const { slideType, chartTitle, rows, split } = props
  const plot = readCutSplit({ segmentRows: rows, segmentSplit: split })
  const labels = split.displayLabels
  const rowsById = new Map(rows.map((row) => [row.groupId, row]))
  const splitRead = split.readStrength ?? null
  const prefix = `${slideType}-split`

  return (
    <div
      data-testid={prefix}
      data-cut-request-id={split.cutRequestId}
      // The static split's own insets (as the gear heatmap's static variant
      // drew them): the title keeps clear of the gear corner, the grid takes
      // the full width its eight columns need.
      style={{
        height: '100%',
        minHeight: 0,
        padding: `${CHART_SEGMENT_GEAR_INSET}px ${SPLIT_PAD_X}px 16px ${SPLIT_PAD_X}px`,
        boxSizing: 'border-box',
      }}
    >
      <HeatGridPlot
        data={plot.data}
        props={{ renderMode: 'static', showImages: 'never' }}
        testIdPrefix={`${prefix}-grid`}
        rowLabelWidth={SPLIT_ROW_LABEL_PX}
        // The split prints its own notes under the read line.
        showNotes={false}
        title={
          <span style={{ display: 'block', paddingRight: CHART_SEGMENT_CONTROL_CLEARANCE_X - SPLIT_PAD_X }}>
            <span data-testid={`${prefix}-chart-title`} style={{ display: 'block' }}>
              {chartTitle}
            </span>
            <span
              data-testid={`${prefix}-title`}
              style={{ display: 'block', marginTop: 4, fontSize: 13, fontWeight: 400, color: SECONDARY }}
            >
              {split.title}
            </span>
          </span>
        }
        rowBadge={(viewRow) => {
          const row = rowsById.get(viewRow.id)
          if (!row) {
            return null
          }
          const read = row.readStrength ?? null
          return (
            <div
              data-testid={`${prefix}-row-meta-${row.groupId}`}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 6,
                rowGap: 3,
                marginTop: 2,
                fontSize: 11,
                lineHeight: 1.3,
                color: MUTED,
              }}
            >
              {/* The base as Python words it, a small group named in it ("(n = 22, small group)"). */}
              <span data-testid={`${prefix}-row-base-${row.groupId}`}>{row.baseText}</span>
              {read ? (
                <ReadPill
                  read={read}
                  label={labels.readStrength[read]}
                  testId={`${prefix}-row-read-${row.groupId}`}
                  style={{ fontSize: 11, padding: '1px 8px' }}
                />
              ) : null}
            </div>
          )
        }}
        footer={
          splitRead || split.readLine || split.notes.length > 0 ? (
            <div data-testid={`${prefix}-read-line`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {splitRead || split.readLine ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  {splitRead ? (
                    <ReadPill read={splitRead} label={labels.readStrength[splitRead]} testId={`${prefix}-read`} />
                  ) : null}
                  {split.readLine ? (
                    <span
                      data-testid={`${prefix}-read-text`}
                      style={{ fontSize: 13, lineHeight: 1.4, color: SECONDARY, paddingTop: 4 }}
                    >
                      {split.readLine}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {split.notes.length > 0 ? (
                <div
                  data-testid={`${prefix}-notes`}
                  style={{ display: 'flex', flexWrap: 'wrap', columnGap: 12, fontSize: 11, lineHeight: 1.4, color: MUTED }}
                >
                  {split.notes.map((note, index) => (
                    <span key={index} data-testid={`${prefix}-note-${index}`}>
                      {note}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null
        }
      />
    </div>
  )
}
